import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
const BO_DY_ORB = 'https://customer-assets.emergentagent.com/job_0e0e64bc-9297-498d-81ba-2e62fc7389a5/artifacts/yq7waw83_Bo-Dy.png';

// Orb avatar used throughout the assistant (header + chat bubbles)
const OrbAvatar = ({ size = 24, className = '' }) => (
  <img
    src={BO_DY_ORB}
    alt="Bo-Dy"
    style={{ width: size, height: size }}
    className={`rounded-full object-cover shrink-0 ${className}`}
    draggable={false}
  />
);
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Sparkles,
  Minimize2,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'bo_dy_session_id';
const OPEN_KEY = 'bo_dy_open';

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function BoDyAssistant() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === '1');
  const [minimized, setMinimized] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Hide on a tiny set of pages where Bo-Dy would conflict with embedded flows
  const HIDE_ON_PATHS = ['/activate-company', '/first-login-password-change'];
  const hideHere = HIDE_ON_PATHS.some((p) => location.pathname.startsWith(p));

  // Fetch welcome + suggestions on first render (works for guests too)
  useEffect(() => {
    if (welcome || hideHere) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/bo-dy/welcome`, {
          credentials: 'include',
          headers: authHeaders(),
        });
        if (r.ok) setWelcome(await r.json());
      } catch (e) {
        // silent
      }
    })();
  }, [welcome, hideHere, user]);

  // Restore previous session messages once when opening
  useEffect(() => {
    if (!open || !sessionId || messages.length > 0) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/bo-dy/sessions/${sessionId}/messages`, {
          credentials: 'include',
          headers: authHeaders(),
        });
        if (r.ok) {
          const data = await r.json();
          setMessages((data.messages || []).map((m) => ({
            id: m.message_id,
            role: m.role,
            content: m.content,
            kb_hits: m.kb_hits || [],
            feedback: m.feedback,
          })));
        }
      } catch (e) {
        // silent
      }
    })();
  }, [open, sessionId, messages.length]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const persistOpen = (val) => {
    setOpen(val);
    localStorage.setItem(OPEN_KEY, val ? '1' : '0');
  };

  // Allow any other component to open Bo-Dy via window.dispatchEvent(new Event('bo-dy:open'))
  useEffect(() => {
    const handler = () => persistOpen(true);
    window.addEventListener('bo-dy:open', handler);
    return () => window.removeEventListener('bo-dy:open', handler);
  }, []);

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setSending(true);
    const optimistic = { id: `tmp_${Date.now()}`, role: 'user', content: trimmed };
    setMessages((m) => [...m, optimistic]);
    setInput('');
    try {
      const r = await fetch(`${API}/api/bo-dy/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Bo-Dy failed to reply');
      }
      const data = await r.json();
      if (data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [
        ...m.filter((x) => x.id !== optimistic.id),
        { id: `usr_${Date.now()}`, role: 'user', content: trimmed },
        { id: data.message_id, role: 'assistant', content: data.reply, kb_hits: data.kb_sources?.map((s) => s.kb_id) || [] },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { id: `err_${Date.now()}`, role: 'assistant', content: `Sorry, I hit an error: ${e.message}. Please try again, or contact your administrator if it persists.` },
      ]);
    } finally {
      setSending(false);
    }
  }, [sessionId, sending]);

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const giveFeedback = async (messageId, helpful) => {
    setMessages((m) => m.map((x) => x.id === messageId ? { ...x, feedback: { helpful } } : x));
    try {
      await fetch(`${API}/api/bo-dy/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message_id: messageId, helpful }),
      });
    } catch (e) {
      // silent
    }
  };

  // Don't render until user is authenticated
  // Bo-Dy is available everywhere (logged-in & anonymous), except a couple of focused flows
  if (hideHere) return null;

  // Floating launcher — orb only, no text
  if (!open) {
    return (
      <button
        onClick={() => persistOpen(true)}
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 ring-2 ring-white/60 ring-offset-2 ring-offset-transparent animate-bo-dy-float"
        data-testid="bo-dy-launcher"
        aria-label="Open Bo-Dy assistant"
        style={{ background: 'radial-gradient(circle at 35% 35%, rgba(255,255,255,0.6), rgba(99,102,241,0.4) 60%, rgba(16,185,129,0.4) 100%)' }}
      >
        <img
          src={BO_DY_ORB}
          alt="Bo-Dy"
          className="w-full h-full rounded-full object-cover drop-shadow-[0_4px_12px_rgba(56,189,248,0.55)]"
          draggable={false}
        />
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-[60] flex flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 transition-all ${
        minimized ? 'w-72 h-14' : 'w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)]'
      }`}
      data-testid="bo-dy-window"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 rounded-t-2xl bg-gradient-to-r from-indigo-600 to-emerald-500 px-4 py-3 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-white/20 shrink-0">
            <OrbAvatar size={36} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate flex items-center gap-1">
              Bo-Dy™ <Sparkles className="w-3 h-3 text-amber-300" />
            </div>
            {!minimized && (
              <div className="text-xs text-white/80 leading-tight truncate">Your BodyCorp365 Community Assistant</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={startNew} className="p-1.5 rounded hover:bg-white/10" title="New conversation" data-testid="bo-dy-new">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={() => setMinimized(!minimized)} className="p-1.5 rounded hover:bg-white/10" title={minimized ? 'Maximize' : 'Minimize'} data-testid="bo-dy-minimize">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => persistOpen(false)} className="p-1.5 rounded hover:bg-white/10" title="Close" data-testid="bo-dy-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="bo-dy-messages">
            {messages.length === 0 && welcome && (
              <div className="space-y-3">
                <div className="flex items-end gap-2">
                  <span className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
                    <OrbAvatar size={24} className="chip" />
                  </span>
                  <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm border border-slate-100 text-sm text-slate-700">
                    {welcome.welcome}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(welcome.suggestions || []).map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-xs bg-white border border-indigo-200 text-indigo-700 rounded-full px-3 py-1.5 hover:bg-indigo-50 transition"
                      data-testid={`bo-dy-suggestion-${s.slice(0, 20)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`bo-dy-msg-${m.role}`}
              >
                {m.role === 'assistant' && (
                  <span className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
                    <OrbAvatar size={24} className="chip" />
                  </span>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl p-3 text-sm shadow-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-tr-sm'
                      : 'bg-white text-slate-800 border border-slate-100 rounded-tl-sm'
                  }`}
                >
                  {m.content}
                  {m.role === 'assistant' && m.kb_hits?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                      {m.kb_hits.map((id) => (
                        <Badge key={id} variant="outline" className="text-[10px] py-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                          {id.replace(/^kb_/, '').replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {m.role === 'assistant' && !m.id.startsWith('err_') && (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => giveFeedback(m.id, true)}
                        className={`p-1 rounded hover:bg-slate-100 ${m.feedback?.helpful === true ? 'text-emerald-600' : 'text-slate-400'}`}
                        title="Helpful"
                        data-testid={`bo-dy-thumb-up-${m.id}`}
                      >
                        <ThumbsUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => giveFeedback(m.id, false)}
                        className={`p-1 rounded hover:bg-slate-100 ${m.feedback?.helpful === false ? 'text-red-600' : 'text-slate-400'}`}
                        title="Not helpful"
                        data-testid={`bo-dy-thumb-down-${m.id}`}
                      >
                        <ThumbsDown className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex items-end gap-2 justify-start">
                <span className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm">
                  <OrbAvatar size={24} className="chip" />
                </span>
                <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm p-3 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2 p-3 border-t border-slate-100 bg-white rounded-b-2xl"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Bo-Dy anything…"
              disabled={sending}
              maxLength={2000}
              data-testid="bo-dy-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={sending || !input.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
              data-testid="bo-dy-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
